import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BarChart3, Clock, TrendingUp, HelpCircle, Calendar } from 'lucide-react';
import { MoneyDisplay } from '../MoneyDisplay';
import { usePaymentMetrics, type PaymentMetrics } from '@/hooks/finance/usePaymentMetrics';
import type { BusinessUnit } from '../BusinessUnitSelector';
import type { Brand } from '../BrandSelector';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PaymentMetricsCardProps {
  businessUnit?: BusinessUnit;
  brand?: Brand;
}

export function PaymentMetricsCard({ 
  businessUnit = 'all', 
  brand = 'all' 
}: PaymentMetricsCardProps) {
  const { data: metrics, isLoading } = usePaymentMetrics(businessUnit, brand);

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (!metrics) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4" />
          Métricas de Pagamento (Histórico Real)
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  Métricas calculadas com base nos últimos 180 dias de pagamentos reais. 
                  Usadas para gerar projeções de fluxo de caixa mais precisas.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {format(parseISO(metrics.periodoAnalise.inicio), 'dd MMM', { locale: ptBR })} - {format(parseISO(metrics.periodoAnalise.fim), 'dd MMM yyyy', { locale: ptBR })}
          <span className="mx-1">•</span>
          {metrics.periodoAnalise.totalPagamentos.toLocaleString()} pagamentos
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Tempo de Pagamento */}
          <MetricItem
            icon={<Clock className="h-4 w-4" />}
            label="Tempo Médio de Pagamento"
            value={
              <span>
                {metrics.medianaDiasAtraso === 0 
                  ? 'No vencimento' 
                  : `${metrics.medianaDiasAtraso} dias após`}
              </span>
            }
            subtext={`Média: ${metrics.mediaDiasAtraso} dias • P75: ${metrics.percentil75DiasAtraso} dias`}
          />

          {/* Ticket Médio */}
          <MetricItem
            icon={<TrendingUp className="h-4 w-4" />}
            label="Ticket Médio"
            value={<MoneyDisplay value={metrics.ticketMedio} showCents={false} />}
            subtext={`Média semanal: $${(metrics.mediaSemanalRecebida / 1000).toFixed(1)}K`}
          />

          {/* Taxa de Realização */}
          <MetricItem
            icon={<BarChart3 className="h-4 w-4" />}
            label="Taxa de Realização"
            value={
              <span className={cn(
                metrics.taxaRealizacao >= 0.95 ? 'text-emerald-600' :
                metrics.taxaRealizacao >= 0.85 ? 'text-amber-600' : 'text-destructive'
              )}>
                {(metrics.taxaRealizacao * 100).toFixed(1)}%
              </span>
            }
            subtext="% do valor acordado que foi pago"
          />

          {/* Curva de Recebimento */}
          <MetricItem
            icon={<BarChart3 className="h-4 w-4" />}
            label="Curva de Recebimento"
            value={<ReceiptCurveBar metrics={metrics} />}
            subtext={`${(metrics.curvaRecebimento.semana0 * 100).toFixed(0)}% na 1ª semana`}
          />
        </div>

        {/* Cenários de Projeção */}
        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-muted-foreground mb-2">Cenários de Projeção</p>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              {metrics.cenarios.pessimista.label}: {(metrics.cenarios.pessimista.taxaRecebimento * 100).toFixed(0)}%
            </Badge>
            <Badge variant="outline" className="text-blue-600 border-blue-300">
              {metrics.cenarios.realista.label}: {(metrics.cenarios.realista.taxaRecebimento * 100).toFixed(0)}%
            </Badge>
            <Badge variant="outline" className="text-emerald-600 border-emerald-300">
              {metrics.cenarios.otimista.label}: {(metrics.cenarios.otimista.taxaRecebimento * 100).toFixed(0)}%
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface MetricItemProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  subtext: string;
}

function MetricItem({ icon, label, value, subtext }: MetricItemProps) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        {icon} {label}
      </p>
      <div className="text-lg font-semibold">{value}</div>
      <p className="text-xs text-muted-foreground">{subtext}</p>
    </div>
  );
}

function ReceiptCurveBar({ metrics }: { metrics: PaymentMetrics }) {
  const curve = metrics.curvaRecebimento;
  
  return (
    <div className="flex h-4 rounded overflow-hidden w-full max-w-[120px]">
      <div 
        className="bg-emerald-500" 
        style={{ width: `${curve.semana0 * 100}%` }}
        title={`Semana 1: ${(curve.semana0 * 100).toFixed(0)}%`}
      />
      <div 
        className="bg-emerald-400" 
        style={{ width: `${curve.semana1 * 100}%` }}
        title={`Semana 2: ${(curve.semana1 * 100).toFixed(0)}%`}
      />
      <div 
        className="bg-amber-400" 
        style={{ width: `${curve.semana2 * 100}%` }}
        title={`Semana 3: ${(curve.semana2 * 100).toFixed(0)}%`}
      />
      <div 
        className="bg-amber-500" 
        style={{ width: `${curve.semana3 * 100}%` }}
        title={`Semana 4: ${(curve.semana3 * 100).toFixed(0)}%`}
      />
      <div 
        className="bg-red-400" 
        style={{ width: `${curve.semana4Plus * 100}%` }}
        title={`Após 4 semanas: ${(curve.semana4Plus * 100).toFixed(0)}%`}
      />
    </div>
  );
}
